import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/MenuRounded';
import Container from '@mui/material/Container';
import MDAvatar from "components/atoms/MDAvatar/MDAvatar";
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { Link, useSearchParams } from "react-router-dom";
import colors from 'assets/theme/base/colors';
import Logo from 'assets/images/logo/JointAi2025Logo.png';
import { MUIColors, Sizes } from 'models/StyleModels';
import MDButton from 'components/atoms/MDButton/MDButton';
import PatientIntakeModal from '../PatientIntakeModal/PatientIntakeModal';
import MDBox from 'components/atoms/MDBox/MDBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse } from '@fortawesome/free-solid-svg-icons';
import ActionSlugs, { IfUserIsAllowed } from 'services/PermissionsService';
import "App.css"
import { useAppSettings } from 'contexts/AppSettingsContext';
import { ProductVersion } from 'models/OrgModels';
import UserMenu from '../UserMenu/UserMenu';

// Display Proper Heading Based on Local Time
const currentDate = new Date();
const curHr = currentDate.getHours();
let headerGreeting = "";

if (curHr < 12) {
	headerGreeting = "Good Morning!";
} else if (curHr < 18) {
	headerGreeting = "Good Afternoon!";
} else {
	headerGreeting = "Good Evening!";
}

interface IResponsiveAppBarProps {
	demoFlow?: boolean | undefined;
}

const ResponsiveAppBar: React.FC<IResponsiveAppBarProps> = ({ demoFlow = false }) => {

	const { homeRoute, productVersion } = useAppSettings();

	const triageFlow = productVersion === ProductVersion.TRIAGE;

	const [searchParams] = useSearchParams();

	const {
		CREATE$APPOINTMENT,
		VIEW$TRIAGE_LIST_PAGE,
	} = ActionSlugs;

	const [anchorElNav, setAnchorElNav] = useState<EventTarget & Element | null>(null);

	const handleOpenNavMenu = (event: React.MouseEvent) => {
		if (demoFlow !== true) {
			setAnchorElNav(event.currentTarget);
		}
	};

	const handleCloseNavMenu = () => {
		if (demoFlow !== true) {
			setAnchorElNav(null);
		}
	};

	const [quickstartModalOpen, setQuickstartModalOpen] = useState<boolean>(false);

	const toggleQuickstartModal = () => {
		if (demoFlow !== true) {
			setQuickstartModalOpen(!quickstartModalOpen);
		}
	};
	const closeModal = () => setQuickstartModalOpen(false);

	// TODO: this rendering logic could use some refactoring and streamlining
	return (
		<>
			{quickstartModalOpen && (
				<PatientIntakeModal
					mode='quickstart'
					closeModal={closeModal}
				/>
			)}
			<AppBar position="static" sx={{ pt: 3, pb: 3 }}>
				<Container maxWidth="sm">
					<Toolbar disableGutters>
						<Typography
							variant="h5"
							noWrap
							sx={{
								mr: 2,
								display: { xs: 'none', md: 'flex' },
							}}>
							<div className='header-greeting-wrapper'>
								<Link to={demoFlow ? { pathname: '/demo/exam-room', search: `?patientName=${searchParams.get('patientName')}` } : homeRoute}>
									<img src={Logo} style={{ height: '60px', width: 'auto' }} alt="Joint AI Logo" />
								</Link>
								<MDBox
									className='header-greeting-text'
									sx={{ display: { lg: 'block', md: 'none' } }}>
									<div className='header-greeting-main'> {headerGreeting} </div>
									<div className='header-greeting-secondary' style={{ color: colors.text.focus }}> Welcome to Joint Ai </div>
								</MDBox>
							</div>
						</Typography>
						<Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
							<IconButton
								size="large"
								aria-label="account of current user"
								aria-controls="menu-appbar"
								aria-haspopup="true"
								onClick={handleOpenNavMenu}
								color="inherit">
								<MenuIcon />
							</IconButton>
							<Menu
								id="menu-appbar"
								anchorEl={anchorElNav}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								keepMounted
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
								open={Boolean(anchorElNav)}
								onClose={handleCloseNavMenu}
								sx={{
									display: { xs: 'block', md: 'none' },
								}}>

								<IfUserIsAllowed to={VIEW$TRIAGE_LIST_PAGE}>
									<Link to={demoFlow ? {} : homeRoute}>
										<MenuItem key="Dashboard" onClick={handleCloseNavMenu}>
											<Typography textAlign="right">Home</Typography>
										</MenuItem>
									</Link>
								</IfUserIsAllowed>


								{!triageFlow && <IfUserIsAllowed to={CREATE$APPOINTMENT}>
									<MenuItem
										key="Quickstart Exam"
										onClick={() => {
											toggleQuickstartModal();
											handleCloseNavMenu();
										}}>
										<Typography textAlign="right">Quickstart Exam</Typography>
									</MenuItem>
								</IfUserIsAllowed>
								}

							</Menu>
						</Box>
						<Typography
							variant="h5"
							noWrap
							sx={{
								mr: 2,
								display: { xs: 'flex', md: 'none' },
								flexGrow: 1,
								fontFamily: 'monospace',
								fontWeight: 700,
								letterSpacing: '.3rem',
								color: 'inherit',
								textDecoration: 'none',
							}}
						>
							<IfUserIsAllowed to={VIEW$TRIAGE_LIST_PAGE}>
								<Link to={demoFlow ? { pathname: '/demo/exam-room', search: `?patientName=${searchParams.get('patientName')}` } : homeRoute}>
									<img src={Logo} style={{ height: '60px', width: 'auto' }} alt="Joint AI Logo" />
								</Link>
							</IfUserIsAllowed>
						</Typography>
						<Box sx={{ justifyContent: "flex-end", flexGrow: 1, pr: 3, display: { xs: 'none', md: 'flex' } }}>
							{!triageFlow && <IfUserIsAllowed to={CREATE$APPOINTMENT}>
								<MDButton
									onClick={toggleQuickstartModal}
									style={{ marginRight: '1rem' }}
									variant='outlined'
									color={MUIColors.SECONDARY}>
									<span style={{ fontWeight: 600, cursor: 'pointer' }}>Quickstart Exam</span>
								</MDButton>
							</IfUserIsAllowed>}
							<IfUserIsAllowed to={VIEW$TRIAGE_LIST_PAGE}>
								<Link to={demoFlow ? { pathname: '/demo/exam-room', search: `?patientName=${searchParams.get('patientName')}` } : homeRoute}>
									<Button
										variant='contained'
										key="Dashboard"
										color="secondary"
										sx={{ color: 'white !important', display: 'block' }}>
										<FontAwesomeIcon icon={faHouse} />
									</Button>
								</Link>
							</IfUserIsAllowed>
						</Box>

						{demoFlow ?
							<IconButton onClick={() => { }} sx={{ p: 0 }}>
								<MDAvatar src={''} size={Sizes.XX_LARGE} alt="Avatar" />
							</IconButton>
							: <UserMenu />
						}

					</Toolbar>
				</Container>
			</AppBar >
		</>
	);
};
export default ResponsiveAppBar;
